import { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "@/App";
import {
  Fingerprint,
  Users,
  Activity,
  Shield,
  BarChart3,
  CheckCircle,
  XCircle,
} from "lucide-react";
import SessionTimer from "@/components/SessionTimer";

const AdminDashboard = () => {
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [complianceChecks, setComplianceChecks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, logsRes, complianceRes] = await Promise.all([
        axios.get("/admin/stats"),
        axios.get("/admin/users"),
        axios.get("/admin/audit-logs?limit=50"),
        axios.get("/admin/compliance-checks?limit=20"),
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data);
      setAuditLogs(logsRes.data);
      setComplianceChecks(complianceRes.data);
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Shield className="w-8 h-8 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          </div>
          <div className="flex items-center space-x-4">
            <SessionTimer enabled={true} />
            <Link
              to="/dashboard"
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Stats Grid */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-10 h-10 text-blue-600" />
              <span className="text-3xl font-bold text-gray-900">
                {stats?.total_users || 0}
              </span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">Total Users</h3>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <Fingerprint className="w-10 h-10 text-purple-600" />
              <span className="text-3xl font-bold text-gray-900">
                {stats?.total_fingerprints || 0}
              </span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">
              Total Fingerprints
            </h3>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <Activity className="w-10 h-10 text-green-600" />
              <span className="text-3xl font-bold text-gray-900">
                {stats?.total_scans_today || 0}
              </span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">Scans Today</h3>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <Shield className="w-10 h-10 text-indigo-600" />
              <span className="text-3xl font-bold text-gray-900">
                {stats?.compliance_rate || 0}%
              </span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">
              Compliance Rate
            </h3>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <Users className="w-6 h-6 mr-2 text-blue-600" />
            All Users
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {u.full_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{u.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          u.role === "admin"
                            ? "bg-purple-100 text-purple-800"
                            : u.role === "business"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {u.role.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {u.organization || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {u.is_active ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Compliance Checks */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <Shield className="w-6 h-6 mr-2 text-blue-600" />
            Recent Compliance Checks
          </h3>
          <div className="space-y-3">
            {complianceChecks.slice(0, 10).map((check) => (
              <div
                key={check.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    {check.status === "passed" ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                    <span className="font-medium text-gray-900">
                      Fingerprint ID: {check.fingerprint_id.substring(0, 8)}...
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-gray-600">
                    Quality Score: {check.quality_score}% | DPI Check:{" "}
                    {check.dpi_check ? "✓" : "✗"}
                  </div>
                  {check.issues && check.issues.length > 0 && (
                    <div className="mt-1 text-xs text-red-600">
                      Issues: {check.issues.join(", ")}
                    </div>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  {new Date(check.checked_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Audit Logs */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <BarChart3 className="w-6 h-6 mr-2 text-blue-600" />
            Recent Activity Logs
          </h3>
          <div className="space-y-2">
            {auditLogs.slice(0, 15).map((log) => (
              <div
                key={log.id}
                className="flex items-center justify-between p-3 border-b border-gray-100 last:border-0"
              >
                <div className="flex-1">
                  <span
                    className={`inline-block px-2 py-1 text-xs font-semibold rounded mr-2 ${
                      log.severity === "CRITICAL"
                        ? "bg-red-100 text-red-800"
                        : log.severity === "WARNING"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    {log.severity}
                  </span>
                  <span className="font-medium text-gray-900">
                    {log.action}
                  </span>
                  <span className="text-gray-600 mx-2">•</span>
                  <span className="text-gray-600">{log.resource}</span>
                  {log.ip_address && (
                    <>
                      <span className="text-gray-600 mx-2">•</span>
                      <span className="text-sm text-gray-500">
                        {log.ip_address}
                      </span>
                    </>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  {new Date(log.timestamp).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
